import isEmpty from 'lodash/isEmpty'
import { useCallback, useState } from 'react'
import React from 'react'
import { Switch } from 'react-router-dom'
import EmployeeShopPopup from '@/components/EmployeeShopPopup'
import RoleGuardedRoute from '@/components/RoleGuardedRoute'
import SentryRoute from '@/components/SentryRoute'
import RefurbedOffers from '@/domains/refurbedOffers'
import Menubar from '@/redesign/domains/layout/components/Menubar/Menubar'
import SidebarMenu from '@/redesign/domains/layout/components/SidebarMenu/SidebarMenu'
import CustomerBlacklistRoutes from '@/redesign/pages/InApp/CustomerBlacklist/customerBlacklist.routes'
import CustomersRoutes from '@/redesign/pages/InApp/Customers/customers.routes'
import DealsList from '@/redesign/pages/InApp/Deals'
import DealDetailPage from '@/redesign/pages/InApp/Deals/[dealId]'
import { CrawlerConfigPage } from '@/redesign/pages/InApp/ProductConfigurator/ProductConfigurator'
import { ERoles } from '@/schemaTypes'
import { useAppSelector } from '@/store'
import styles from '@/styles/app.module.scss'
import AuctionsRoutes from './Auctions/auctions.routes'
import cashBookRoutes from './CashBook/cashBook.routes'
import CustomDealFormsRoutes from './Configurations/CustomDealForms/customDealForms.routes'
import ItemCategoriesRoutes from './Configurations/ItemCategories/itemCategories.routes'
import ItemQuestionsRoutes from './Configurations/ItemQuestions/itemQuestions.routes'
import ResponseTemplatesRoutes from './Configurations/ResponseTemplates/responseTemplates.routes'
import StorageFacilitiesRoutes from './Configurations/StorageFacilities/storageFacilities.routes'
import ConfigurationsRoutes from './Configurations/configurations.routes'
import CustomDealsRoutes from './CustomDeals/customDeals.routes'
import EmployeeChangePassword from './EmployeeChangePassword'
import ImportExportsRoutes from './ImportExport/importExport.routes'
import ItemsRoutes from './Items/items.routes'
import PrivateSalesRoutes from './PrivateSales/privateSales.routes'
import ProductsRoutes from './Products/products.routes'
import ProductPropertyDefinitionsRoutes from './product-property-definitions/productPropertyDefinitions.routes'

type Props = {
  onLogout: () => void
}

export const InApp = React.memo((props: Props) => {
  const currentUser = useAppSelector((state) => state.user.current)
  const _employeeActiveShop = useAppSelector(
    (state) => state.ui.employeeActiveShop,
  )

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [activeShopSelected, setActiveShopSelected] = useState(false)
  const [activeShopModalManuallyOpened, setActiveShopModalManuallyOpened] =
    useState(false)

  const activeShopModalOpen =
    (!activeShopSelected &&
      currentUser?.company?._id &&
      isEmpty(_employeeActiveShop)) ||
    activeShopModalManuallyOpened

  const _closeShowEmployeeChangeShop = () => {
    setActiveShopSelected(true)
    setActiveShopModalManuallyOpened(false)
  }

  const toggleDrawerOpen = useCallback(() => {
    setIsDrawerOpen((prevState) => !prevState)
  }, [])

  const onShowedEmployeeChangeShopPopup = useCallback(() => {
    setActiveShopModalManuallyOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  return (
    <div className="grid grid-rows-[min-content,auto] p-3 min-h-screen">
      <Menubar
        currentUser={currentUser}
        onLogout={props.onLogout}
        onSideMenuOpen={toggleDrawerOpen}
        onShowedEmployeeChangeShopPopup={onShowedEmployeeChangeShopPopup}
      />

      <div>
        <div>
          <SidebarMenu
            visible={isDrawerOpen}
            onClose={closeDrawer}
            currentUser={currentUser}
          />
        </div>
        <div className={`${styles.stretch} h-full`}>
          <Switch>
            <SentryRoute
              path="/inApp/change-password"
              component={EmployeeChangePassword}
            />
            <SentryRoute
              path="/inApp/deals/:dealId"
              component={DealDetailPage}
            />
            <SentryRoute
              path="/inApp/customDeals"
              component={CustomDealsRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/customDealForms"
              component={CustomDealFormsRoutes}
            />
            <SentryRoute path="/inApp/customers" component={CustomersRoutes} />
            <SentryRoute path="/inApp/cashBook" component={cashBookRoutes} />
            <RoleGuardedRoute
              minRole={ERoles.Superadmin}
              path="/inApp/importExport"
              component={ImportExportsRoutes}
            />
            {/* Configuration pages open to admins; keep them before the configurations guard. */}
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/configurations/storageFacilities"
              component={StorageFacilitiesRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/configurations/itemQuestions"
              component={ItemQuestionsRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/configurations/itemCategories"
              component={ItemCategoriesRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/configurations/responseTemplates"
              component={ResponseTemplatesRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/configurations/customDealForms"
              component={CustomDealFormsRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/configurations/refurbedOffers"
              component={RefurbedOffers}
            />
            <RoleGuardedRoute
              minRole={ERoles.Superadmin}
              path="/inApp/configurations"
              component={ConfigurationsRoutes}
            />
            {/* <SentryRoute path="/inApp/profile/" render={props => <ProfilePage {...props} employeeId={this.props.currentUser?._id} />} /> */}
            {/* <SentryRoute path="/inApp/userAccount" render={props => <UserAccount {...props} employeeId={this.props.currentUser?._id} />} /> */}
            <SentryRoute path="/inApp/items" component={ItemsRoutes} />
            <RoleGuardedRoute
              minRole={ERoles.Locationadmin}
              path="/inApp/auctions"
              component={AuctionsRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/privatesales"
              component={PrivateSalesRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/product-property-definitions"
              component={ProductPropertyDefinitionsRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Admin}
              path="/inApp/products"
              component={ProductsRoutes}
            />
            <RoleGuardedRoute
              minRole={ERoles.Superadmin}
              path="/inApp/productconfigurator"
              component={CrawlerConfigPage}
            />
            <SentryRoute
              path="/inApp/customerBlacklists"
              component={CustomerBlacklistRoutes}
            />
            <SentryRoute path="/" component={DealsList} />
          </Switch>
        </div>
      </div>
      {activeShopModalOpen && (
        <EmployeeShopPopup onClose={_closeShowEmployeeChangeShop} />
      )}
    </div>
  )
})
